import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { Organization } from "../../../../core/models/entities/Organization";
import { Profile } from "../../../../core/models/entities/Profile";
import { PageQuery } from "../../../../utils/models/pageQuery";
import { QueryChanger } from "../../../shared/hooks/usePageQuery";
import FunctionalList from "../../../components/GenericTable/FunctionalList";
import { useFunctionalListLogosViewModel } from "../vmHooks/useFunctionalListLogosViewModel";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import { useLogoActionHandler } from "../hooks/useLogoActionsHandler";
import { useApprovalDialogByRole } from "../../../components/Dialogs/useApprovalDialogByRole";
import { useCreateLogoDialog } from "../../customer-list/hooks/useCreateLogoDialog";
import { FilterChangeEvent } from "../../../components/GenericTable/SubComponents/TableHeader/ListHeader";
import React from "react";
import { useHasChainLogos } from "../hooks/useHasChainLogos";
import { Role } from "../../../../core/models/entities/Role";

interface WrapperProps {
  profile: Profile;
  logos?: BaseLogo[];
  isPageLoading: boolean;
  lastPage: number;
  options: LogoOptions;
  query: PageQuery;
  queryChanger: QueryChanger;
  customers: Organization[];
  vendors?: Organization[];

  onSelectLogo: (logo: BaseLogo) => void;
  onViewRejectNote: (logo: BaseLogo) => void;
  updatingLogoId?: number;
}

const CHAIN_LOGO_CLASSIFICATION = "11";
const NUMBER_LOGO_CLASSIFICATION = "6";
const NAME_LOGO_CLASSIFICATION = "2";

export function LogoListWrapper({
  profile,
  logos,
  isPageLoading,
  lastPage,
  options,
  query,
  queryChanger,
  customers,
  vendors,
  onSelectLogo,
  onViewRejectNote,
  updatingLogoId,
}: WrapperProps) {
  const {
    handleSkipApproval,
    handleSkipAddToCart,
    handleCommitAndInitiateApproval,
  } = useLogoActionHandler();
  const { component, start: openInitApproval } =
    useApprovalDialogByRole<BaseLogo>({
      role: profile.userOrganizationInformations.role,
      subject: "logo",
      onSkip: (entity) => handleSkipApproval(entity).then(),
      onOrder: (entity) => handleSkipAddToCart(entity, 10).then(),
      onInitApproval: (entity, email) =>
        handleCommitAndInitiateApproval(entity, {}, { email }).then(),
    });

  const { component: componentCreateLogoDialog, open } = useCreateLogoDialog({
    onContinue: async () => {},
    onCancel: () => {},
    options: options,
  });

  function openCreateLogoDialog() {
    open();
  }

  const classificationFilter = query.filterBy?.find(
    (f) => f.field === "classification"
  );
  const currentClassification = classificationFilter?.value;

  const isVendor = profile.userOrganizationInformations.role >= Role.Vendor;
  const hasChainLogos = useHasChainLogos(
    profile.userOrganizationInformations.role,
    isVendor
  );

  const shouldShowToggle =
    profile.detailedConfigurations.organizationCountry === "DK";

  const handleClassificationChange = (classification: string | null) => {
    const newFilters =
      query.filterBy?.filter((f) => f.field !== "classification") || [];

    if (classification) {
      newFilters.push({
        field: "classification",
        value: classification,
      });
    }

    queryChanger.handleFilterChange({
      activeFilters: newFilters.map((f) => ({
        key: f.field,
        value: f.value,
      })),
    } as FilterChangeEvent);
  };

  const vm = useFunctionalListLogosViewModel(
    profile,
    isPageLoading,
    lastPage,
    options,
    query,
    queryChanger,
    onSelectLogo,
    onViewRejectNote,
    openInitApproval,
    openCreateLogoDialog,
    customers,
    vendors,
    logos,
    updatingLogoId
  );

  // Logo Type Toggle "Pill" Component
  const LogoTypeToggle = () => (
    <div className="flex items-center ml-4">
      <div className="flex bg-gray-100 rounded-full p-1 border border-borderGray">
        <button
          onClick={() => handleClassificationChange(null)}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
            !currentClassification
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          Alle Logoer
        </button>
        {hasChainLogos && (
          <button
            onClick={() =>
              handleClassificationChange(CHAIN_LOGO_CLASSIFICATION)
            }
            className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
              currentClassification === CHAIN_LOGO_CLASSIFICATION
                ? "bg-white text-primary-600 border border-borderGray"
                : "text-primary-700"
            }`}
          >
            Kædelogoer
          </button>
        )}
        <button
          onClick={() => handleClassificationChange(NUMBER_LOGO_CLASSIFICATION)}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
            currentClassification === NUMBER_LOGO_CLASSIFICATION
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          Talpakker
        </button>
        <button
          onClick={() => handleClassificationChange(NAME_LOGO_CLASSIFICATION)}
          className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${
            currentClassification === NAME_LOGO_CLASSIFICATION
              ? "bg-white text-primary-600 border border-borderGray"
              : "text-primary-700"
          }`}
        >
          Navne
        </button>
      </div>
    </div>
  );

  // Combine title with toggle
  const tableTitle = (
    <div className="flex items-center font-bold">
      {typeof vm.tableTitle === "string"
        ? vm.tableTitle.toUpperCase()
        : vm.tableTitle}
      {shouldShowToggle && <LogoTypeToggle />}
    </div>
  );

  const vmWithToggle = {
    ...vm,
    tableTitle: tableTitle,
    center: vm.center, // Restore original center (likely empty now)
  };

  return (
    <>
      {componentCreateLogoDialog}
      {component}
      <FunctionalList<BaseLogo> {...vmWithToggle} />
    </>
  );
}
